import React from 'react';
import { Box, Typography, Container, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';

const sections = [
  {
    title: '1. Introduction',
    paragraphs: [
      'Filmhook Mediaapps Pvt Ltd ("Filmhook Apps") provides a platform dedicated to professionals and aspiring talent in the film, media, and entertainment industries.',
      'To maintain a professional, secure, and legally compliant environment, we enforce strict age suitability requirements for all our users.'
    ]
  },
  {
    title: '2. Minimum Age Requirement',
    paragraphs: [
      'The minimum age to create an account, access, or use the Filmhook Apps platform is 18 years old.',
      'By using our services, you represent and warrant that you are at least 18 years of age and possess the legal authority, right, and freedom to enter into a binding agreement based on our Terms of Service.'
    ],
    footerParagraph: 'If you are under 18, you are strictly prohibited from using Filmhook Apps.'
  },
  {
    title: '3. Why We Restrict Access to 18+',
    paragraphs: [
      'Filmhook Apps restricts its user base to adults for several key reasons:'
    ],
    bullets: [
      'Professional Networking: Our platform is designed for career building, recruitment, and business networking in the film industry, which requires users to have the legal capacity to enter into contracts and professional engagements.',
      'Content Suitability: While we enforce strict content guidelines, the nature of film production, scriptwriting, and acting auditions may occasionally involve mature themes unsuitable for minors.',
      'Legal Compliance: We adhere to global privacy laws (like COPPA and GDPR-K) and app store regulations that protect minors from targeted advertising, data collection, and unsolicited contact.'
    ]
  },
  {
    title: '4. Age Verification & Enforcement',
    paragraphs: [
      'We reserve the right to request proof of age at any stage to verify compliance with our age suitability policy.',
      'If we discover that a user is under 18 years of age:'
    ],
    bullets: [
      'The account will be immediately suspended or permanently deleted.',
      'All associated personal data will be purged from our systems, subject to legal retention obligations.',
      'Any ongoing subscriptions or services will be terminated.'
    ]
  },
  {
    title: '5. Reporting Underage Users',
    paragraphs: [
      'We rely on our community to help maintain a safe environment. If you believe a user on Filmhook Apps is under the age of 18, please report them immediately using our in-app reporting tools or by contacting our support team at support@filmhookapps.com.'
    ],
    footerParagraph: 'All reports are taken seriously and investigated promptly.'
  }
];

const AgeSuitability = () => {
  return (
    <Box sx={{ py: { xs: 4, sm: 8, md: 12 }, minHeight: '80vh' }}>
      <Container maxWidth="md">
        <Box
          sx={{
            background: 'var(--bg-card)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid var(--border-color)',
            borderRadius: '24px',
            p: { xs: 2.5, sm: 4, md: 7 },
            boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
          }}
        >
          <Typography variant="h3" fontWeight={800} gutterBottom sx={{ color: 'var(--text-heading)', textAlign: 'center', mb: 1, fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' } }}>
            Age Suitability
          </Typography>
          <Typography variant="h6" sx={{ color: '#5dade2', textAlign: 'center', mb: 3, fontWeight: 500, letterSpacing: '0.5px', fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' } }}>
            Age Requirements and Restrictions
          </Typography>
          <Box sx={{ width: 80, height: 3, background: 'linear-gradient(90deg, #07a4f5, #5dade2)', mx: 'auto', mb: 6, borderRadius: '2px' }} />

          {sections.map((section, idx) => (
            <Box key={idx} sx={{ mb: 6 }}>
              <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: 'var(--text-heading)', mb: 2, fontSize: { xs: '1.2rem', sm: '1.35rem', md: '1.5rem' } }}>
                {section.title}
              </Typography>
              
              {section.paragraphs.map((p, pIdx) => (
                <Typography key={pIdx} variant="body1" sx={{ color: 'var(--text-secondary)', lineHeight: 1.8, mb: 2, textAlign: 'justify', fontSize: { xs: '0.88rem', md: '1rem' } }}>
                  {p}
                </Typography>
              ))}

              {section.bullets && (
                <List sx={{ pl: 2, mb: 2 }}>
                  {section.bullets.map((bullet, bIdx) => (
                    <ListItem key={bIdx} alignItems="flex-start" disableGutters sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: '32px', mt: '8px' }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#5dade2' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ color: 'var(--text-secondary)', lineHeight: 1.7, textAlign: 'justify', fontSize: { xs: '0.88rem', md: '1rem' } }}>
                            {bullet}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}

              {section.footerParagraph && section.footerParagraph.split('\n').map((fp, fpIdx) => (
                <Typography key={fpIdx} variant="body1" sx={{ color: 'var(--text-secondary)', lineHeight: 1.8, mt: 2, textAlign: 'justify', fontSize: { xs: '0.88rem', md: '1rem' } }}>
                  {fp}
                </Typography>
              ))}

              {idx < sections.length - 1 && (
                <Box sx={{ height: '1px', background: 'var(--border-subtle)', mt: 5 }} />
              )}
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default AgeSuitability;
